import Card from "../Card"
import SmallHeadingComponent from "../SmallHeadingComponent"
import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../redux/store/hooks"
import {
    selectDashboardDataSeverities,
    selectDashboardSelectedPatient,
    selectMedicationEntries,
    selectMedicationRisks,
    selectMedicationTimestamp
} from "../../redux/ProfessionalDashboard"
import Button from "../Button"
import notNull from "../../util/notNull"
import MedicationQuestionnaireBuilder from "../../medication_questionnaire/questionnaires/MedicationQuestionnaireBuilder"
import SmallestHeadingComponent from "../SmallestHeadingComponent"
import {
    DateAPI, FetchSingleQuestionnaireResponsePayload,
    FetchSingleQuestionnaireSpecification,
    GetPatientMedicationInformationSpecification,
    GetPatientMedicationRisksSpecification,
    InsertMedicationQuestionnaireInputSpecification,
    InsertPatientMedicationRisksSpecification, MathAPI,
    MedicationEntry,
    MedicationIndicatorNames,
    MedicationIndicators,
    QuestionnaireTimestamp,
    ValueType
} from "pokal_api"
import expect from "../../util/expect"
import { showNotification } from "../../redux/Notification"
import { dispatchThunk } from "../../redux/thunk/thunkify"
import unique from "../../util/unique"
import LineSplitter from "../LineSplitter"
import ProgressBar from "../ProgressBar"
import {
    PatientPreConditionQuestions,
    PatientPreConditions,
    PatientPreConditionSections
} from "../../medication_questionnaire/questions/PatientHasPreCondition"
import MedicationDose from "../../medication_questionnaire/abstract/MedicationDose"
import MedicationQuestionnaireInput from "../../medication_questionnaire/abstract/MedicationQuestionnaireInput"
import MedicationQuestionnaire from "../../medication_questionnaire/abstract/MedicationQuestionnaire"
import MedicationQuestion from "../../medication_questionnaire/abstract/MedicationQuestion"
import MedicationAnswer from "../../medication_questionnaire/abstract/MedicationAnswer"
import ParentControlledCheckBox from "../ParentControlledCheckBox"
import { PatientSFQValues } from "../../medication_questionnaire/questions/PatientHasSFQValue"
import CheckBox from "../CheckBox"
import DocumentationComponent from "../DocumentationComponent"
import { VALUE_TYPE_PHQ_DOC } from "../../documentation/ValueTypeDocumentation"
import { mockPatientUsed, selectOnboardingIsRunningState, selectOnboardingStep } from "../../redux/Onboarding"
import mockPatient from "../../onboarding/mock/mockPatient"

function MedicationQuestionnaireView() {

    const DEBUG = false
    const DEBUG_QUESTION_INDEX: null | number = null

    const medication = useAppSelector(selectMedicationEntries)
    const patient = useAppSelector(selectDashboardSelectedPatient)

    const running  = useAppSelector(selectOnboardingIsRunningState)
    const step     = useAppSelector(selectOnboardingStep)

    // ─── use mock patient while the onboarding is active ──────────────────────────────
    const selectedPatient = mockPatientUsed(running, step) ? mockPatient()[0] : patient

    const risks = useAppSelector(selectMedicationRisks)
    const timestamp = useAppSelector(selectMedicationTimestamp)

    const [phqScore, setPhqScore] = useState<number | null>(null)
    const [patientDoses, setPatientDoses] = useState<MedicationDose[]>([])
    const [patientCoMedicationDoeses, setPatientCoMedicationDoses] = useState<
        MedicationDose[]
    >([])

    const [patientSfqValues, setPatientSfqValues] = useState<Record<PatientSFQValues, boolean> | null>(null)

    const [symptomsImproved, setSymptomsImproved] = useState<boolean>(false)

    const [patientPreconditions, setPatientPreconditions] = useState<
        Record<PatientPreConditions, boolean>
    >({
        [PatientPreConditions.v1]: false,
        [PatientPreConditions.v2]: false,
        [PatientPreConditions.v3]: false,
        [PatientPreConditions.v4]: false,
        [PatientPreConditions.v5]: false,
        [PatientPreConditions.v6]: false,
        [PatientPreConditions.v7]: false,
        [PatientPreConditions.v8]: false,
        [PatientPreConditions.v9]: false,
        [PatientPreConditions.v10]: false,
        [PatientPreConditions.v11]: false,
        [PatientPreConditions.v12]: false,
        [PatientPreConditions.v13]: false,
        [PatientPreConditions.v14]: false,
        [PatientPreConditions.v15]: false,
        [PatientPreConditions.v16]: false,
        [PatientPreConditions.v17]: false,
        [PatientPreConditions.v18]: false,
        [PatientPreConditions.v19]: false,
        [PatientPreConditions.v20]: false,
        [PatientPreConditions.v21]: false
    })

    const chainedPreConditions: Map<
        PatientPreConditions,
        PatientPreConditions[]
    > = new Map()

    chainedPreConditions.set(PatientPreConditions.v8, [
        PatientPreConditions.v10
    ])

    function updatePatientPreCondition(
        condition: PatientPreConditions,
        value: boolean
    ) {
        if (value) {
            const toEnable: PatientPreConditions[] = [condition]
            const tmp = chainedPreConditions.get(condition)
            if (tmp) toEnable.push(...tmp)
            const updated = patientPreconditions
            for (const elem of toEnable) updated[elem] = true
            setPatientPreconditions({ ...updated })
        } else {
            setPatientPreconditions({
                ...patientPreconditions,
                [condition]: value
            })
        }
    }

    const [questionnaireInput, setQuestionnaireInput] =
        useState<MedicationQuestionnaireInput | null>(null)

    const [showDuplicateTimestampWarning, setShowDuplicateTimestampWarning] =
        useState<boolean>(false)
    useEffect(() => {
        if (notNull(risks)) {
            if (risks[timestamp].indicators.length > 0) {
                setShowDuplicateTimestampWarning(true)
                return
            }
        }
        setShowDuplicateTimestampWarning(false)
    }, [timestamp, risks])

    useEffect(() => {
        if (medication) {
            const doses: MedicationDose[] = []
            for (const entry of medication.medication) {
                doses.push({ ...entry, dose: 0 })
            }
            setPatientDoses(sortDoses(doses))
            setPatientCoMedicationDoses(
                medication.coMedication.map((elem) => ({
                    ...elem,
                    dose: 0
                }))
            )
        }
    }, [medication])

    function reset() {
        setQuestionnaireInput(null)
        setCurrentQuestion(null)
        setCurrentIndex(null)
        setCurrentQuestionnaire(null)
        setQuestionHistory([])
    }

    useEffect(() => {
        reset()
    }, [medication, selectedPatient])

    const [showHistory, setShowHistory] = useState<boolean>(false)

    function onSubmitQuestionnaireInput() {
        const confirm = window.confirm(
            "Sind Sie sicher, dass Sie alle Informationen korrekt eingegeben haben und fortfahren wollen?"
        )
        if (confirm && medication && selectedPatient) {
            setQuestionnaireInput(
                new MedicationQuestionnaireInput(
                    patientDoses,
                    patientCoMedicationDoeses,
                    selectedPatient,
                    timestamp,
                    phqScore,
                    {
                        ...patientPreconditions
                    },
                    patientSfqValues ? {
                        ...patientSfqValues
                    } : null,
                    symptomsImproved
                )
            )
        }
    }

    type QuestionHistoryEntry = {
        question: string
        answer: string
        automated: boolean
        triggers: MedicationIndicators[]
    }
    const [currentQuestionnaire, setCurrentQuestionnaire] =
        useState<MedicationQuestionnaire | null>(null)
    const [currentIndex, setCurrentIndex] = useState<number | null>(null)
    const [currentQuestion, setCurrentQuestion] =
        useState<MedicationQuestion | null>(null)
    const [questionHistory, setQuestionHistory] = useState<
        QuestionHistoryEntry[]
    >([])

    function appendToQuestionHistory(entry: QuestionHistoryEntry) {
        setQuestionHistory([...questionHistory, entry])
    }
    const [indicators, setIndicators] = useState<
        {
            indicator: MedicationIndicators
            problematicATCs: string[]
            args: string[]
        }[]
    >([])
    function appendToIndicators(answer: MedicationAnswer) {
        const { withoutArgs, withArgs } = answer.getTriggeredIndicators()
        for (const indicator of withArgs) {
            const duplicate = indicators.find(
                (i) => i.indicator === indicator.indicator
            )
            if (duplicate === undefined) {
                setIndicators([
                    ...indicators,
                    {
                        indicator: indicator.indicator,
                        problematicATCs: answer.getProblematicACTs(),
                        args: indicator.args
                    }
                ])
                return
            }
            const acts: string[] = unique([
                ...duplicate.problematicATCs,
                ...answer.getProblematicACTs()
            ])
            setIndicators([
                ...indicators.filter((elem) => elem !== duplicate),
                {
                    indicator: indicator.indicator,
                    problematicATCs: acts,
                    args: indicator.args
                }
            ])
        }
        for (const indicator of withoutArgs) {
            const duplicate = indicators.find((i) => i.indicator === indicator)
            if (duplicate === undefined) {
                setIndicators([
                    ...indicators,
                    {
                        indicator,
                        problematicATCs: answer.getProblematicACTs(),
                        args: []
                    }
                ])
                return
            }
            const acts: string[] = unique([
                ...duplicate.problematicATCs,
                ...answer.getProblematicACTs()
            ])
            setIndicators([
                ...indicators.filter((elem) => elem !== duplicate),
                {
                    indicator,
                    problematicATCs: acts,
                    args: []
                }
            ])
        }
    }

    //TODO: Replace 0 with question index for debugging
    useEffect(() => {
        if (notNull(questionnaireInput)) {
            const questionnaire =
                MedicationQuestionnaireBuilder(questionnaireInput)
            setCurrentQuestionnaire(questionnaire)
            setCurrentIndex(DEBUG && DEBUG_QUESTION_INDEX !== null ? DEBUG_QUESTION_INDEX : 0)
            setCurrentQuestion(questionnaire[DEBUG && DEBUG_QUESTION_INDEX !== null ? DEBUG_QUESTION_INDEX : 0](questionnaireInput))
        }
    }, [questionnaireInput])

    useEffect(() => {
        if (notNull(currentQuestion) && notNull(questionnaireInput)) {
            if (currentQuestion.isAnsweredAutomatically(questionnaireInput)) {
                const answer = currentQuestion
                    .getAutomatedAnswer(questionnaireInput)
                    .addProblematicACTs(currentQuestion.getProblematicACTs())
                answer.pushACTsToChildren()
                handleAnswer(answer, true)
            }
        }
    }, [currentQuestion])

    function handleManualAnswer(answer: string) {
        const question = expect(currentQuestion)
        handleAnswer(question.getAnswerByName(answer), false)
    }

    const dispatch = useAppDispatch()

    function onDone() {
        if (notNull(selectedPatient) && notNull(medication)) {
            if (indicators.length === 0 && medication.medication.length > 0) {
                indicators.push({
                    indicator: MedicationIndicators.NO_RISK,
                    problematicATCs: [],
                    args: []
                })
            }
            InsertPatientMedicationRisksSpecification.request({
                patient: selectedPatient.id,
                timestamp: timestamp,
                risks: indicators
            }).then(() => {
                InsertMedicationQuestionnaireInputSpecification.request({
                    patient: selectedPatient.id,
                    timestamp,
                    preconditions: {
                        ...patientPreconditions
                    }
                }).then(() => {
                    reset()
                    showNotification(
                        dispatch,
                        "Fragebogen abgeschickt",
                        "Der Fragebogen wurde erfolgreich verarbeitet."
                    )
                    dispatchThunk(
                        dispatch,
                        GetPatientMedicationRisksSpecification,
                        {
                            patient: selectedPatient.id
                        }
                    )
                    dispatchThunk(
                        dispatch,
                        GetPatientMedicationInformationSpecification,
                        {
                            patient: selectedPatient.id
                        }
                    )
                })
            })
        }
    }

    const [submitQuestionnaireRequired, setSubmitQuestionnaireRequired] = useState<boolean>(false)

    function handleAnswer(answer: MedicationAnswer, automated: boolean) {
        const question = expect(currentQuestion)
        const questionnaire = expect(currentQuestionnaire)
        appendToQuestionHistory({
            answer: answer.get(),
            automated,
            question: question.get(),
            triggers: [
                ...answer.getTriggeredIndicators().withoutArgs,
                ...answer
                    .getTriggeredIndicators()
                    .withArgs.map((elem) => elem.indicator)
            ]
        })
        appendToIndicators(answer)
        if (answer.isFinalAnswer()) {
            setSubmitQuestionnaireRequired(true)
            return
        }
        if (answer.hasFollowingQuestion()) {
            const next = answer.expectFollowingQuestion()
            setCurrentQuestion(next)
        } else if (question.hasFollowingQuestion()) {
            console.log(
                "Question has following question and is taken as next question."
            )
            const next = question.expectFollowingQuestion()
            setCurrentQuestion(next)
        } else {
            console.log("No next question detected, jumping to next section.")
            const index = expect(currentIndex)
            if (index < questionnaire.length - 1) {
                setCurrentQuestion(
                    questionnaire[index + 1](expect(questionnaireInput))
                )
                setCurrentIndex(index + 1)
            } else {
                console.log("No next section available. Questionnaire ended.")
                setSubmitQuestionnaireRequired(true)
            }
        }
    }

    function onChangeDose(entry: MedicationEntry, doseStr: string) {
        try {
            const dose = Number(doseStr)
            if (!isNaN(dose)) {
                const doses = patientDoses.filter(
                    (elem) => elem.id !== entry.id
                )
                setPatientDoses(sortDoses([...doses, { ...entry, dose }]))
            }
        } catch {}
    }

    function sortDoses(doses: MedicationDose[]) {
        return doses.sort((d1, d2) => {
            if (d1.name < d2.name) {
                return -1
            }
            if (d1.name > d2.name) {
                return 1
            }
            return 0
        })
    }

    const severities = useAppSelector(selectDashboardDataSeverities)
    const [latestSFQ, setLatestSFQ] = useState<null | FetchSingleQuestionnaireResponsePayload>(null)
    const [latestPHQ, setLatestPHQ] = useState<null | FetchSingleQuestionnaireResponsePayload>(null)

    useEffect(() => {
        if (!severities) return
        const latest = severities.last[ValueType.PHQUESTIONNAIRE]
        if (!latest) return
        FetchSingleQuestionnaireSpecification.request({
            questionnaire: latest.id,
            type: ValueType.PHQUESTIONNAIRE
        }).then((res) => {
            setLatestPHQ(res)
            if(timestamp !== QuestionnaireTimestamp.T0) {
                setPhqScore(MathAPI.sum(res.answers))
            } else {
                setPhqScore(null)
            }
        })
    }, [severities, timestamp])

    useEffect(() => {
        if (!severities) return
        const latest = severities.last[ValueType.SFQUESTIONNAIRE]
        if (!latest) return
        const submission = new Date(latest.date)
        if (!DateAPI.datesIsWithinLastHours(submission, 24 * 7 * 12)) return
        FetchSingleQuestionnaireSpecification.request({
            questionnaire: latest.id,
            type: ValueType.SFQUESTIONNAIRE
        }).then((res) => {
            setLatestSFQ(res)
        })
    }, [severities])

    useEffect(() => {
        if(!latestSFQ)
            return
        setPatientSfqValues({
            [PatientSFQValues.v1]: latestSFQ.answers[0] > 0,
            [PatientSFQValues.v2]: latestSFQ.answers[1] > 0,
            [PatientSFQValues.v3]: latestSFQ.answers[2] > 0,
            [PatientSFQValues.v4]: latestSFQ.answers[3] > 0
        })
    }, [latestSFQ])

    return (
        <Card width={"100%"} fill={false}>
            <div className={"flex flex-col"}>
                <SmallHeadingComponent>
                    {`Medikationsfragebogen ${!questionnaireInput ? "Teil 1" : "Teil 2"}`}
                </SmallHeadingComponent>
                {selectedPatient === null && (
                    <div className={"italic"}>
                        Bitte wählen Sie eine Patient:in aus um den Fragebogen
                        beantworten zu können.
                    </div>
                )}
                {notNull(selectedPatient) &&
                    notNull(medication) &&
                    !questionnaireInput && (
                        <div className={"flex flex-col"}>
                            <div className={"flex flex-col"}>
                                {showDuplicateTimestampWarning && (
                                    <>
                                        <div
                                            className={
                                                "font-bold text-rose-600"
                                            }>
                                            Achtung: Für diesen Zeitpunkt liegen
                                            bereits Einträge vor.
                                        </div>
                                    </>
                                )}
                            </div>
                            {timestamp !== QuestionnaireTimestamp.T0 && (
                                <div className={"flex flex-col mt-8 space-y-4"}>
                                    <SmallestHeadingComponent>
                                        <DocumentationComponent documentation={VALUE_TYPE_PHQ_DOC}>PHQ-8</DocumentationComponent>
                                    </SmallestHeadingComponent>

                                    <div className={"flex space-x-4 items-center"}>
                                        <input
                                            className={"rounded w-20"}
                                            type={"text"}
                                            value={phqScore === null ? "" : phqScore}
                                            onChange={(e) =>
                                                setPhqScore(Number(e.target.value) <= 0 ? null : Number(e.target.value))
                                            }
                                        />
                                        {latestPHQ ? (
                                            <div className={""}>
                                                Aktuellster Eintrag vom {new Date(latestPHQ.date).toLocaleDateString("de")}
                                            </div>
                                        ) : (
                                            <div className={""}>
                                                Aktuell noch kein Eintrag vorhanden
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className={"flex flex-col space-y-4 mt-8"}>
                                <SmallestHeadingComponent>
                                    Antidepressiva-Tagesdosis
                                </SmallestHeadingComponent>
                                <div className={"flex flex-col space-y-2"}>
                                    {Array.from(patientDoses).map((entry) => (
                                        <div
                                            key={entry.id}
                                            className={
                                                "flex space-x-2 items-center"
                                            }>
                                            <div className={"w-[200px]"}>
                                                {entry.name}
                                            </div>
                                            <input
                                                className={"rounded w-20"}
                                                value={entry.dose === 0 ? "" : entry.dose}
                                                onChange={(e) =>
                                                    onChangeDose(
                                                        entry,
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <div>mg pro Tag</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={"flex flex-col space-y-4 mt-4"}>
                                <SmallestHeadingComponent>Verbesserung der Symptomatik</SmallestHeadingComponent>
                                <ParentControlledCheckBox
                                    onChange={(val) => setSymptomsImproved(val)}
                                    value={symptomsImproved}>
                                    Gibt es Anzeichen für eine klinisch relevante Verbesserung der Symptomatik?
                                </ParentControlledCheckBox>
                            </div>
                            {timestamp === QuestionnaireTimestamp.T0 && (
                                <div className={"flex flex-col space-y-4 mt-4"}>
                                    <SmallestHeadingComponent>
                                        Krankheitsbild
                                    </SmallestHeadingComponent>

                                    {PatientPreConditionSections.map(
                                        (section, index) => (
                                            <div
                                                className={
                                                    "flex flex-col space-y-4 pb-10"
                                                }
                                                key={index}>
                                                {section.title && (
                                                    <div className={"text-xl"}>{section.title}</div>
                                                )}
                                                {section.preconditions.map(
                                                    (p) => (
                                                        <ParentControlledCheckBox
                                                            key={p}
                                                            onChange={(val) =>
                                                                updatePatientPreCondition(
                                                                    p,
                                                                    val
                                                                )
                                                            }
                                                            value={
                                                                patientPreconditions[
                                                                    p
                                                                ]
                                                            }>
                                                            {
                                                                PatientPreConditionQuestions[
                                                                    p
                                                                ]
                                                            }
                                                        </ParentControlledCheckBox>
                                                    )
                                                )}
                                            </div>
                                        )
                                    )}
                                </div>
                            )}
                            <div className={"mt-10"}>
                                <Button onClick={onSubmitQuestionnaireInput}>
                                    Weiter zu Fragebogen Teil 2
                                </Button>
                            </div>
                        </div>
                    )}
                {showHistory && (
                    <div className={"space-y-4"}>
                        <SmallestHeadingComponent>
                            Fragen-Historie
                        </SmallestHeadingComponent>
                        <div className={"flex flex-col space-y-4"}>
                            {questionHistory.map((elem, index) => (
                                <div key={index}>
                                    <div className={"flex space-x-2"}>
                                        <div>{elem.question}</div>
                                        <div className={"font-bold"}>
                                            {elem.answer}
                                        </div>
                                    </div>
                                    <div className={"italic"}>
                                        Die Frage wurde automatisch beantwortet:{" "}
                                        {elem.automated ? "Ja" : "Nein"}
                                    </div>
                                    <div>
                                        <div className={"flex space-x-2"}>
                                            <div className={"font-bold"}>
                                                Trigger:
                                            </div>
                                            {elem.triggers.length === 0 && (
                                                <div>Keine</div>
                                            )}
                                        </div>
                                        <ul className={"list-disc ml-4"}>
                                            {elem.triggers
                                                .map(
                                                    (t) =>
                                                        MedicationIndicatorNames[
                                                            t
                                                        ]
                                                )
                                                .map((indicator) => (
                                                    <li
                                                        key={`${index}.${indicator.id}`}>
                                                        <div className={"flex"}>
                                                            {`${indicator.id}) ${indicator.title}`}
                                                        </div>
                                                        <div>
                                                            {
                                                                indicator.description
                                                            }
                                                        </div>
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {notNull(questionnaireInput) &&
                    notNull(currentIndex) &&
                    notNull(currentQuestionnaire) &&
                    notNull(currentQuestion) && !submitQuestionnaireRequired && (
                        <div className={"flex flex-col space-y-4"}>
                            <ProgressBar
                                current={currentIndex}
                                of={currentQuestionnaire.length}
                            />
                            {DEBUG && (
                                <CheckBox
                                    initial={false}
                                    onChange={(val) => setShowHistory(val)}>
                                    Historie anzeigen
                                </CheckBox>
                            )}
                            <LineSplitter>{currentQuestion.get()}</LineSplitter>
                            <div className={"flex space-x-4"}>
                                {currentQuestion
                                    .getPossibleAnswers()
                                    .map((a) => (
                                        <Button
                                            key={a}
                                            onClick={() =>
                                                handleManualAnswer(a)
                                            }>
                                            {a}
                                        </Button>
                                    ))}
                            </div>
                        </div>
                    )}
                {submitQuestionnaireRequired && (
                    <div className={"flex flex-col space-y-4"}>
                        <div>Der Fragebogen wurde erfolgreich ausgefüllt. Alle automatischen Daten wurden übertragen. Bitte klicken Sie jetzt auf Weiter.</div>
                        <Button onClick={onDone}>Weiter</Button>
                    </div>
                )}
            </div>
        </Card>
    )
}

export default MedicationQuestionnaireView
